'use client';
import { ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '@/store';
import { useRouter, usePathname } from 'next/navigation';
import {
  toggleRTL,
  toggleTheme,
  toggleMenu,
  toggleLayout,
  toggleAnimation,
  toggleNavbar,
  toggleSemidark,
} from '@/store/themeConfigSlice';
import { getTranslation } from '@/i18n';
import { useSession } from '@/hooks';
import { LINKS } from '@/constants';

interface IProps {
  children?: ReactNode;
}

const Container = ({ children }: IProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const themeConfig = useSelector((state: IRootState) => state.themeConfig);
  const dispatch = useDispatch();
  const { initLocale } = getTranslation();
  const { validateSession } = useSession();

  useEffect(() => {
    let isMounted = true;

    const handleSession = async () => {
      const isPortalRoute = pathname.startsWith('/portal');
      const isAuthRoute =
        pathname === LINKS.login.route ||
        pathname === LINKS.forgotPassword.route;

      if (!isPortalRoute && !isAuthRoute) {
        return;
      }

      const hasStoredToken =
        typeof window !== 'undefined' && !!localStorage.getItem('token');

      if (!hasStoredToken) {
        if (isPortalRoute && isMounted) {
          router.replace(LINKS.login.route);
        }
        return;
      }

      const isValid = await validateSession();
      if (!isMounted) {
        return;
      }

      if (isValid && isAuthRoute) {
        router.replace(LINKS.dashboard.route);
      }

      if (!isValid && isPortalRoute) {
        router.replace(LINKS.login.route);
      }
    };

    handleSession();

    return () => {
      isMounted = false;
    };
  }, [pathname, router, validateSession]);

  useEffect(() => {
    dispatch(toggleTheme(localStorage.getItem('theme') || themeConfig.theme));
    dispatch(toggleMenu(localStorage.getItem('menu') || themeConfig.menu));
    dispatch(
      toggleLayout(localStorage.getItem('layout') || themeConfig.layout),
    );
    dispatch(
      toggleRTL(localStorage.getItem('rtlClass') || themeConfig.rtlClass),
    );
    dispatch(
      toggleAnimation(
        localStorage.getItem('animation') || themeConfig.animation,
      ),
    );
    dispatch(
      toggleNavbar(localStorage.getItem('navbar') || themeConfig.navbar),
    );
    dispatch(
      toggleSemidark(localStorage.getItem('semidark') || themeConfig.semidark),
    );
    // locale
    initLocale(themeConfig.locale);
  }, [
    dispatch,
    initLocale,
    themeConfig.theme,
    themeConfig.menu,
    themeConfig.layout,
    themeConfig.rtlClass,
    themeConfig.animation,
    themeConfig.navbar,
    themeConfig.locale,
    themeConfig.semidark,
  ]);
  return (
    <div
      className={`${(themeConfig.sidebar && 'toggle-sidebar') || ''} ${
        themeConfig.menu
      } ${themeConfig.layout} ${
        themeConfig.rtlClass
      } main-section relative font-nunito text-sm font-normal antialiased`}>
      {children}
    </div>
  );
};

export default Container;
